import { Home, Search, ShoppingCart, MessageCircle, User } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCart } from '@/contexts/CartContext';
import { useUnreadCount } from '@/hooks/useChat';
import { useSettings } from '@/contexts/SettingsContext';
import { motion } from 'framer-motion';

const BottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { totalItems } = useCart();
  const unreadCount = useUnreadCount();
  const { t } = useSettings();

  const tabs = [
    { path: '/', icon: Home, label: t('nav.home') },
    { path: '/search', icon: Search, label: t('nav.search') },
    { path: '/chat', icon: MessageCircle, label: t('nav.chat') },
    { path: '/cart', icon: ShoppingCart, label: t('nav.cart') },
    { path: '/profile', icon: User, label: t('nav.profile') },
  ];

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 px-4 pb-2 safe-bottom pointer-events-none">
      <nav className="pointer-events-auto mx-auto max-w-lg bg-card/85 backdrop-blur-2xl rounded-2xl border border-border/30 shadow-depth">
        <div className="flex items-center justify-around h-[62px]">
          {tabs.map((tab) => {
            const isActive = location.pathname === tab.path;
            const Icon = tab.icon;
            return (
              <button
                key={tab.path}
                onClick={() => navigate(tab.path)}
                className="relative flex flex-col items-center justify-center w-14 h-full gap-0.5"
              >
                <motion.div
                  className="relative"
                  whileTap={{ scale: 0.8 }}
                  transition={{ type: 'spring', stiffness: 500 }}
                >
                  {isActive && (
                    <motion.div
                      layoutId="navPill"
                      className="absolute inset-0 -m-2 rounded-xl bg-primary/10"
                      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                    />
                  )}
                  <Icon
                    size={20}
                    className={`relative z-10 transition-all duration-300 ${
                      isActive ? 'text-primary' : 'text-muted-foreground'
                    }`}
                    strokeWidth={isActive ? 2.5 : 1.8}
                  />

                  {/* Chat badge */}
                  {tab.path === '/chat' && unreadCount > 0 && (
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute -top-1.5 -start-2 gradient-primary text-primary-foreground text-[8px] font-black rounded-full min-w-[15px] min-h-[15px] flex items-center justify-center z-20 border-2 border-card"
                    >
                      {unreadCount}
                    </motion.span>
                  )}

                  {/* Cart badge */}
                  {tab.path === '/cart' && totalItems > 0 && (
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute -top-1.5 -start-2 gradient-primary text-primary-foreground text-[8px] font-black rounded-full min-w-[15px] min-h-[15px] flex items-center justify-center z-20 border-2 border-card"
                    >
                      {totalItems}
                    </motion.span>
                  )}
                </motion.div>

                <span className={`text-[9px] font-bold transition-all duration-300 ${
                  isActive ? 'text-[hsl(24,95%,35%)]' : 'text-muted-foreground'
                }`}>
                  {tab.label}
                </span>

                {/* Active top line */}
                {isActive && (
                  <motion.div
                    layoutId="navLine"
                    className="absolute top-0 left-1/2 -translate-x-1/2 w-6 h-[2.5px] gradient-primary rounded-full"
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                  />
                )}
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default BottomNav;
